/**
 * @fileoverview Input/output schemas for the extract-world-elements-v3 composite agent.
 *
 * This composite orchestrates parallel detection (core + rare) followed by
 * sequential enrichment (detailer). The input accepts raw project data;
 * the flow handles genre resolution and formatting internally.
 */

import { z } from 'zod';
import { OutputSchema as DetailerOutputSchema, type Output as DetailerOutput } from
  '@/ai/agents/primitives/story-import/world-element-detailer-v3/schema';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Input for the composite flow.
 *
 * Composites carry only the orchestration-log metadata (logId, model,
 * projectId). They have no customPrompt — children create their own input
 * bags with their own logIds and prompts.
 */
export const InputSchema = z.object({
  logId: z.string(),
  model: z.string(),
  projectId: z.string().optional(),
  textInput: z.string().describe('The full text to extract world elements from.'),
  genreTemplateIds: z.array(z.string()).optional().describe(
    'Genre template IDs from the project (e.g., "fantasy-high-epic"). Used to resolve element priorities.'
  ),
  existingElementNames: z.array(z.string()).optional().describe(
    'Names of already-existing world elements to skip during detection.'
  ),
  loreContext: z.any().optional().describe('Lore context object for the project.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output is the same as the detailer's output — enriched elements with descriptions.
 */
export const OutputSchema = DetailerOutputSchema;
export type Output = DetailerOutput;
